import React from 'react';
import {
  VisuallyHidden,
  Spinner,
  StatusBanner,
  ParagraphText,
  StackLayout,
  Box,
} from '@leagueplatform/genesis-core';
import { useParams } from 'react-router-dom';
import { UseBaseQueryResult } from '@tanstack/react-query';
import { useIntl } from '@leagueplatform/locales';
import type {
  AnnotatedJsonSchema,
  EnvironmentKey,
  DomainEntity,
  EntityStatus,
  EntityStaticMessage,
} from '@web-config-app/core';
import { EntityStatusValue, buildTenantPath } from '@web-config-app/core';
import {
  bannerAnimation,
  BannerLinkItem,
  EntityDetailsPageLayout,
  ErrorBanner,
  ErrorBoundaryFullPageWrapper,
  ErrorPage,
  IconButton,
  LoadingOverlayModal,
  LocaleSelect,
  SuccessMessage,
} from '@web-config-app/core-react-ui';
import {
  EntityForm,
  EntityTreeView,
  createErrorFilteringMiddleware,
  useEntityFormState,
} from '@web-config-app/entity-form';
import {
  defaultRenderers,
  ruleCombinationJsonLogicInputControlRenderer,
} from '@web-config-app/entity-form-control-renderers';
import {
  useConfigUserFeedback,
  useEntityData,
  useSetEntityData,
  useLocales,
  useValidationSettings,
  useConfigApp,
} from '@web-config-app/core-react';
import type { GetEntityDetailsPathForEnvAndId } from '@web-config-app/core-react';
import { useResolvedConfigFeedback } from './use-resolved-config-feedback';
import { EntityDetailsHeaderContainer } from '../../containers/entity-details-header-container/entity-details-header.container';
import { EntityDataTableModalContainer } from '../../containers/entity-data-table-modal-container/entity-data-table-modal.container';
import {
  EntityFormSkeleton,
  SidePanelSkeleton,
} from './entity-details-skeleton.component';
import { EntityJsonModalContainer } from '../../containers/entity-json-modal-container/entity-json-modal.container';
import { EntityAuditLogContainer } from '../../containers/entity-audit-log-container/entity-audit-log-container';

const useSidePanelExpanded = (): [boolean, () => void] => {
  const [isExpanded, setIsExpanded] = React.useState<boolean>(false);
  const toggle = React.useCallback(() => {
    setIsExpanded((previousIsExpanded) => !previousIsExpanded);
  }, []);
  return [isExpanded, toggle];
};

export type EntityDetailsPageProps = Pick<
  UseBaseQueryResult,
  'isError' | 'isLoading'
> & {
  formData: any;
  formSchema?: AnnotatedJsonSchema;
  formPath?: string;
  formReady: boolean;
  domainEntity: DomainEntity;
  entityListPath: string;
  entityTree: any;
  onFormDataChange: (updatedData: any) => void;
  formDataChangeDebounceLength?: number;
  handleNodeToggle?: (nodeId: string) => void;
  expandedNodes?: string[];
  publishDangerously: boolean;
  environmentName: string;
  entityStatus?: EntityStatus;
  environmentKey: EnvironmentKey;
  isReadOnly?: boolean;
  getEntityDetailsPathForEnvAndId: GetEntityDetailsPathForEnvAndId;
  setEntityName: (name: string) => void;
  entityStaticMessage?: EntityStaticMessage;
  isEntitySingleEnv?: boolean;
  localizable?: boolean;
  defaultLocale: string;
  instanceId?: string;
};

export const EntityDetailsPage: React.FC<EntityDetailsPageProps> = ({
  isLoading,
  isError: isFullPageError,
  formData,
  formSchema,
  formPath,
  formReady,
  domainEntity,
  entityListPath,
  entityTree,
  handleNodeToggle,
  onFormDataChange,
  expandedNodes,
  publishDangerously,
  entityStatus,
  environmentName,
  formDataChangeDebounceLength = 400,
  environmentKey,
  isReadOnly,
  getEntityDetailsPathForEnvAndId,
  setEntityName,
  entityStaticMessage,
  localizable,
  isEntitySingleEnv: showEntitySingleEnvBanner,
  instanceId,
  defaultLocale,
}) => {
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  const [isSidePanelExpanded, setIsSidePanelExpanded] = useSidePanelExpanded();
  const { tenantId: detailsTenantId } = useParams<{ tenantId?: string }>();
  const { formatMessage } = useIntl();
  const entityName =
    domainEntity && formatMessage({ id: domainEntity.labelKey }, { count: 1 });

  const {
    entityOperationLoadingMsg,
    configMessages,
    resetConfigMessages,
    removeConfigMessage,
  } = useConfigUserFeedback((state) => state);

  const entityRootData = useEntityData();
  const setEntityData = useSetEntityData();

  const { formDataChangeHandler, localData, localSchema } = useEntityFormState({
    onFormDataChange,
    formDataChangeDebounceLength,
    formPath,
    formData,
    formSchema,
  });

  const {
    showErrorBanner,
    hideErrorBanner,
    errorBannerTitle,
    errorBannerContents,
    additionalFormErrors,
    resolveBackendPointerFields,
    entityTreeNodeMap,
    rootSchemaForLabels,
  } = useResolvedConfigFeedback({
    domainEntity,
    entityName,
    formPath,
    formSchema,
  });

  const { errorFilterKeywords, shouldShowFormValidation } =
    useValidationSettings();

  const { setErrorFilterKeywordsActionOverride } = useConfigUserFeedback(
    (state) => ({
      setErrorFilterKeywordsActionOverride:
        state.setErrorFilterKeywordsActionOverride,
    }),
  );

  const { currentLocale, setLocale, hasMultipleLocales, locales } =
    useLocales();

  const { frontendValidation } = useConfigApp();

  /**
   * Avoid rendering the EntityForm until we have the data setup. This prevents
   * issues with some controls that need to calculate complex internal state (ex: Rules combination, rich text)
   * setting up with the wrong data.
   */
  React.useEffect(() => {
    if (formReady) {
      setIsInitialized(true);
    }
  }, [formReady]);

  const entityFormMiddleware = React.useMemo(
    () => createErrorFilteringMiddleware(errorFilterKeywords),
    [errorFilterKeywords],
  );

  // Cleanup: when leaving the page, restore tenant defaults by clearing any override
  // This is needed because the override is in global state and would affect other entities (especially if the tenant has live validation)
  React.useEffect(
    () => () => {
      setErrorFilterKeywordsActionOverride?.(null);
    },
    [setErrorFilterKeywordsActionOverride],
  );

  const domainEntityRenderers = React.useMemo(
    () => [
      ...defaultRenderers,
      ruleCombinationJsonLogicInputControlRenderer,
      ...(domainEntity.controlRenderers ?? []),
    ],
    [domainEntity.controlRenderers],
  );

  if (isFullPageError) {
    return <ErrorPage homeUrl={buildTenantPath(detailsTenantId)} />;
  }

  const successMessages =
    configMessages?.filter((message) => message.type === 'success') ?? [];

  const warningMessages =
    configMessages?.filter((message) => message.type === 'warning') ?? [];

  // Showing PublishDangerouslyBanner when the config is not a readonly config
  const showPublishDangerouslyBanner =
    publishDangerously &&
    entityStatus === EntityStatusValue.Published &&
    !isReadOnly;

  const showEntityStaticMessageBanner =
    entityStaticMessage?.status && entityStaticMessage?.description;
  const isFormInitializing = isLoading || isInitialized === false;

  // a readOnly entity will usually have a staticMessage attached that prioritize showing instead of the default readonly banner
  const showReadOnlyBanner = isReadOnly && !showEntityStaticMessageBanner;

  // only show the locale select if there are multiple locales available on the tenant and the entity is localizable
  const showLocaleSelect = localizable && locales && hasMultipleLocales;

  const isAuditLogEnabled = entityStatus !== 'new' && instanceId;

  return (
    <EntityDetailsPageLayout
      isSidePanelExpanded={isSidePanelExpanded}
      topBar={
        <EntityDetailsHeaderContainer
          setEntityName={setEntityName}
          isLoading={isLoading}
          domainEntity={domainEntity}
          entityListPath={entityListPath}
          environmentKey={environmentKey}
          environmentName={environmentName}
          publishDangerously={publishDangerously}
          getEntityDetailsPathForEnvAndId={getEntityDetailsPathForEnvAndId}
        />
      }
      statusBanners={
        <div aria-live="polite">
          {showEntityStaticMessageBanner && (
            <StatusBanner
              status={entityStaticMessage.status}
              css={{ animation: bannerAnimation }}
            >
              <ParagraphText>{entityStaticMessage.description}</ParagraphText>
            </StatusBanner>
          )}
          {showPublishDangerouslyBanner && (
            <StatusBanner
              data-testid="publish-dangerously-banner"
              showIcon={false}
              status="warning"
              css={{ animation: bannerAnimation }}
            >
              <ParagraphText>
                {formatMessage(
                  { id: 'PUBLISHING_DANGEROUSLY_STATUS_BANNER' },
                  { environmentName },
                )}
              </ParagraphText>
            </StatusBanner>
          )}
          {showReadOnlyBanner && (
            <StatusBanner
              data-testid="readonly-config-banner"
              showIcon={false}
              status="warning"
              css={{ animation: bannerAnimation }}
            >
              <ParagraphText>
                {formatMessage(
                  { id: 'READONLY_CONFIG_STATUS_BANNER' },
                  { entity: entityName },
                )}
              </ParagraphText>
            </StatusBanner>
          )}
          {showEntitySingleEnvBanner && (
            <StatusBanner
              data-testid="entity-single-environment-banner"
              status="info"
              css={{ animation: bannerAnimation }}
            >
              <ParagraphText>
                {formatMessage(
                  { id: 'ENTITY_SINGLE_ENVIRONMENT_STATUS_BANNER' },
                  {
                    environment: environmentName,
                  },
                )}
              </ParagraphText>
            </StatusBanner>
          )}
          {/* TODO:: COMBINE THESE BANNERS INTO ONE COMPONENT WITH
          https://everlong.atlassian.net/browse/CACT-1093?assignee=60e614d1bd7f5d0068ace1dd */}
          {warningMessages.length > 0 && (
            <StatusBanner
              status="warning"
              title={formatMessage({ id: 'WARNING' })}
              css={{ animation: bannerAnimation }}
              onDismissClick={() => resetConfigMessages()}
            >
              <StackLayout
                as="ul"
                spacing="$half"
                css={{
                  'li::marker': {
                    fontSize: '1rem',
                  },
                  listStyleType: 'revert',
                  marginInlineStart: '$one',
                  marginBlockStart: '$quarter',
                  marginBlock: 0,
                  typography: '$bodyOne',
                  color: '$onSurfaceTextPrimary',
                }}
              >
                {warningMessages.map((message) => {
                  const resolved = resolveBackendPointerFields(message);
                  return (
                    <li key={message.id}>
                      <BannerLinkItem
                        detail={resolved.text ?? message.text}
                        path={resolved.path}
                      />
                    </li>
                  );
                })}
              </StackLayout>
            </StatusBanner>
          )}
          {successMessages.length > 0 && (
            <StatusBanner
              status="success"
              title={formatMessage({ id: 'SUCCESS' })}
              css={{ animation: bannerAnimation }}
              onDismissClick={() => resetConfigMessages()}
            >
              <Box
                as="ul"
                css={{
                  marginBlock: 0,
                  listStyleType: successMessages.length > 1 ? 'revert' : 'none',
                }}
              >
                {successMessages.map((message) => (
                  <SuccessMessage
                    key={message.id}
                    removeConfigMessage={removeConfigMessage}
                    message={message}
                  />
                ))}
              </Box>
            </StatusBanner>
          )}
          {showErrorBanner && (
            <ErrorBanner
              title={errorBannerTitle}
              onDismissClick={hideErrorBanner}
              defaultLocale={defaultLocale}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...errorBannerContents}
            />
          )}
        </div>
      }
      sidePanel={
        isLoading ? (
          <SidePanelSkeleton />
        ) : (
          <Box
            css={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              height: '100%',
            }}
          >
            <Box
              css={{
                display: 'flex',
                justifyContent: 'flex-end',
                paddingInline: '$half',
                paddingBlockStart: '$half',
                flex: '0 0 auto',
              }}
            >
              <IconButton
                priority="tertiary"
                quiet
                size="toolbar"
                icon={
                  isSidePanelExpanded
                    ? 'interfaceChevronLeft'
                    : 'interfaceChevronRight'
                }
                label={formatMessage({
                  id: isSidePanelExpanded
                    ? 'COLLAPSE_SIDE_PANEL'
                    : 'EXPAND_SIDE_PANEL',
                })}
                onClick={setIsSidePanelExpanded}
              />
            </Box>
            <EntityTreeView
              entityTree={entityTree}
              onSelect={handleNodeToggle}
              activeTreeNodeId={formPath}
              expandedItems={expandedNodes}
              isSidePanelExpanded={isSidePanelExpanded}
            />
            <Box
              css={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '$threeQuarters',
                paddingInline: '$oneAndHalf',
                paddingBlock: '$threeQuarters',
                flex: '0 0 auto',
              }}
            >
              {isAuditLogEnabled && (
                <EntityAuditLogContainer
                  entityType={entityName}
                  domainEntityType={domainEntity.type}
                  entityInstanceId={instanceId}
                  environmentKey={environmentKey}
                  entityTreeNodeMap={entityTreeNodeMap}
                  rootSchema={rootSchemaForLabels}
                />
              )}
              <EntityDataTableModalContainer />
            </Box>
          </Box>
        )
      }
    >
      <>
        {Boolean(entityOperationLoadingMsg) && (
          <LoadingOverlayModal
            isOpen={Boolean(entityOperationLoadingMsg)}
            loadingText={`${entityOperationLoadingMsg}`}
          >
            <ParagraphText aria-hidden>
              {entityOperationLoadingMsg}
            </ParagraphText>
          </LoadingOverlayModal>
        )}
        <VisuallyHidden>
          <Spinner
            loading={isLoading}
            loadingText={formatMessage({ id: 'LOADING' })}
          />
        </VisuallyHidden>
        {isFormInitializing ? (
          <EntityFormSkeleton />
        ) : (
          <>
            {showLocaleSelect && (
              <LocaleSelect
                availableLocales={locales}
                onSelectLocale={setLocale}
                currentLocale={currentLocale}
              />
            )}
            <ErrorBoundaryFullPageWrapper>
              <EntityJsonModalContainer
                onSetJsonClick={(data) => {
                  /**
                   * This is a kinda... let's say "naughty" way to
                   * force the form to re-render with new data.
                   */
                  setIsInitialized(false);
                  setEntityData?.(data);
                  // running a setTimeout with 0ms will
                  // push the state update to the end of the event loop
                  // by which time the data will have been updated
                  // in the store and passed down here
                  setTimeout(() => {
                    setIsInitialized(true);
                  }, 0);
                }}
                entityData={entityRootData}
              />
              <EntityForm
                renderers={domainEntityRenderers}
                schema={localSchema}
                data={localData}
                onDataChange={formDataChangeHandler}
                showValidation={shouldShowFormValidation}
                middleware={entityFormMiddleware}
                readOnly={
                  isReadOnly || entityStatus === EntityStatusValue.Archived
                }
                allowedUriProtocols={frontendValidation.uriSchemePolicy.allowed}
                additionalErrors={additionalFormErrors}
              />
            </ErrorBoundaryFullPageWrapper>
          </>
        )}
      </>
    </EntityDetailsPageLayout>
  );
};
